import React, { useState, useEffect, useMemo } from 'react';
import { fetchAdminScopes, updateAdminScopes } from '../../api/admin_scopes';
import { fetchAiSettings, updateAiSettings } from '../../api/changelog';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';

const ChangelogSettings = () => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(null);

  // AI style guide
  const [aiFeedback, setAiFeedback] = useState('');
  const [aiFeedbackLoaded, setAiFeedbackLoaded] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);

  useEffect(() => {
    fetchAdminScopes()
      .then(data => setAdminUsers(data.admins || []))
      .catch(() => {})
      .finally(() => setLoading(false));

    fetchAiSettings()
      .then(text => setAiFeedback(text || ''))
      .catch(() => {})
      .finally(() => setAiFeedbackLoaded(true));
  }, []);

  const saveAiFeedback = async () => {
    setSavingAi(true);
    setAiSaved(false);
    try {
      await updateAiSettings(aiFeedback);
      setAiSaved(true);
      setTimeout(() => setAiSaved(false), 2500);
    } catch {
      window.ALERT?.error('Failed to save AI style guide.');
    } finally {
      setSavingAi(false);
    }
  };

  const changelogUsers = useMemo(() =>
    adminUsers.filter(u => (u.scopes || '').split(',').includes(ADMIN_SCOPES.MANAGE_CHANGELOG)),
    [adminUsers]
  );

  const filteredAll = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return adminUsers.filter(u =>
      (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)
    );
  }, [adminUsers, searchQuery]);

  const hasChangelogAccess = user =>
    (user.scopes || '').split(',').filter(Boolean).includes(ADMIN_SCOPES.MANAGE_CHANGELOG);

  const toggleAccess = async user => {
    const currentScopes = (user.scopes || '').split(',').filter(Boolean);
    const hasAccess = currentScopes.includes(ADMIN_SCOPES.MANAGE_CHANGELOG);
    const newScopes = hasAccess
      ? currentScopes.filter(s => s !== ADMIN_SCOPES.MANAGE_CHANGELOG)
      : [...currentScopes, ADMIN_SCOPES.MANAGE_CHANGELOG];

    setSaving(user.id);
    try {
      await updateAdminScopes(user.id, newScopes.join(','));
      setAdminUsers(prev =>
        prev.map(u => u.id === user.id ? { ...u, scopes: newScopes.join(',') } : u)
      );
    } catch (e) {
      window.ALERT?.error('Failed to update permissions.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className='cl-settings'>
      <div className='cl-settings-section'>
        <h2 className='cl-settings-heading'>Changelog Editors</h2>
        <p className='cl-settings-subtext'>
          Admins with edit access can create, update, and delete changelog entries.
        </p>

        {loading ? (
          <div className='cl-settings-loading'>Loading...</div>
        ) : (
          <>
            <div className='cl-settings-search-row'>
              <input
                className='cl-settings-search'
                type='text'
                placeholder='Search admin by name or email...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {searchQuery && filteredAll.length > 0 && (
              <div className='cl-settings-results'>
                {filteredAll.map(user => (
                  <div key={user.id} className='cl-settings-user-row'>
                    <div className='cl-settings-user-info'>
                      <span className='cl-settings-user-name'>{user.name || user.email}</span>
                      <span className='cl-settings-user-email'>{user.email}</span>
                    </div>
                    <button
                      className={`cl-settings-toggle-btn${hasChangelogAccess(user) ? ' active' : ''}`}
                      onClick={() => toggleAccess(user)}
                      disabled={saving === user.id}
                    >
                      {saving === user.id ? '...' : hasChangelogAccess(user) ? 'Remove Access' : 'Grant Access'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {searchQuery && filteredAll.length === 0 && (
              <p className='cl-settings-empty'>No admins found matching "{searchQuery}".</p>
            )}

            <div className='cl-settings-current'>
              <div className='cl-settings-current-label'>Current editors ({changelogUsers.length})</div>
              {changelogUsers.length === 0 ? (
                <p className='cl-settings-empty'>No editors yet. Search above to grant access.</p>
              ) : (
                changelogUsers.map(user => (
                  <div key={user.id} className='cl-settings-user-row'>
                    <div className='cl-settings-user-info'>
                      <span className='cl-settings-user-name'>{user.name || user.email}</span>
                      <span className='cl-settings-user-email'>{user.email}</span>
                    </div>
                    <button
                      className='cl-settings-toggle-btn active'
                      onClick={() => toggleAccess(user)}
                      disabled={saving === user.id}
                    >
                      {saving === user.id ? '...' : 'Remove Access'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <div className='cl-settings-section'>
        <h2 className='cl-settings-heading'>AI Style Guide</h2>
        <p className='cl-settings-subtext'>
          Notes for the AI when generating drafts from Linear issues. Use this to refine
          tone, voice, or specific wording rules. Examples: <em>"Avoid the word 'streamline'."</em>{' '}
          <em>"Keep titles under 60 characters."</em>{' '}
          <em>"Always speak in active voice."</em>
        </p>

        {!aiFeedbackLoaded ? (
          <div className='cl-settings-loading'>Loading...</div>
        ) : (
          <>
            <textarea
              className='cl-ai-feedback-textarea'
              value={aiFeedback}
              onChange={e => setAiFeedback(e.target.value)}
              placeholder='Add style guidance for AI-generated drafts...'
              maxLength={5000}
              rows={8}
            />
            <div className='cl-ai-feedback-footer'>
              <span className='cl-ai-feedback-count'>{aiFeedback.length} / 5000</span>
              <div className='cl-ai-feedback-actions'>
                {aiSaved && <span className='cl-ai-feedback-saved'>Saved ✓</span>}
                <button
                  className='cl-ai-feedback-save'
                  onClick={saveAiFeedback}
                  disabled={savingAi}
                >
                  {savingAi ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangelogSettings;
